import { Badge, Button, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import {
  User,
  UserStatus,
  PhoneNumberVerificationStatus,
} from '@eppendorf/vnls-user-tenant-utils';
import { ReactElement, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAccessControlGuard } from '$shared/custom-hooks/useAccessControlGuard';
import { useTokenAttributes } from '$shared/custom-hooks/useTokenAttributes';
import { getLocalizedDate } from '$shared/utils/date.utils';

import { InfoItem } from '$features/devices/device-information/info-item';
import { InfoList } from '$features/devices/device-information/info-list';
import { stringifyUserRoles } from '$features/users/roles.utils';
import { useResendInvite } from '$features/users/users.api';

export interface EditViewProps {
  onEdit: () => void;
  user: User;
  onVerifyPhoneNumberClick: () => void;
}

type UserDisplayField = {
  label: string;
  name: keyof User | string;
  customValue?: (user: User) => string | ReactElement | null;
};

export function ReadView({
  onEdit,
  user,
  onVerifyPhoneNumberClick,
}: EditViewProps): ReactElement {
  const { t } = useTranslation();
  const [inviteResent, setInviteResent] = useState(false);

  const { mutateAsync, isError, isPending } = useResendInvite();

  const { email } = useTokenAttributes(['email']);
  const canEditOwnName = useAccessControlGuard('UPDATE_OWN_FIRST_NAME');
  const canEditOthersName = useAccessControlGuard('UPDATE_OTHERS_FIRST_NAME');
  const canResendInvite = useAccessControlGuard('INVITE_USER');
  const canEdit = user.email === email ? canEditOwnName : canEditOthersName;

  async function handleInviteResend(): Promise<void> {
    try {
      if (user) await mutateAsync(user.id);
      setInviteResent(true);
    } catch {
      // eslint-disable-next-line no-useless-return -- disables test error output
      return;
    }
  }

  let displayedUserFields: UserDisplayField[] = [
    {
      label: t('userManagement.firstName'),
      name: 'firstName',
    },
    {
      label: t('userManagement.lastName'),
      name: 'lastName',
    },
    {
      label: t('shared.email'),
      name: 'email',
    },
    {
      label: t('userManagement.role'),
      name: 'role',
      customValue: () => `${stringifyUserRoles(user.roles || [])}`,
    },
    {
      label: t('userManagement.invitedAt'),
      name: 'formattedInviteSentAt',
      // eslint-disable-next-line react/no-unstable-nested-components -- its fine
      customValue: () =>
        inviteResent ? (
          <div className="flex flex__ai--center">
            <Icon
              name="success"
              size={IconSizeClasses.XSmall}
              className="bg-green-500 flex m-right-s"
            />

            <p className="color-green-500 flex font-weight-bold">
              {t('userInvite.inviteResentSuccessfully')}
            </p>
          </div>
        ) : (
          <div className="flex flex__dir--column flex__ai--flex-start">
            <span>{`by ${user.createdBy} on ${
              user.inviteSentAt ? getLocalizedDate(user.inviteSentAt) : ''
            }`}</span>
            {canResendInvite && user.status === UserStatus['Invite Sent'] && (
              <Button
                variant="tertiary"
                size="small"
                onClick={() => handleInviteResend()}
                disabled={isPending || isError}
              >
                <span>{t('userManagement.resendInvite')}</span>
              </Button>
            )}
          </div>
        ),
    },
  ];

  displayedUserFields = [
    ...displayedUserFields.slice(0, 3),
    {
      label: t('userManagement.phoneNumber'),
      name: 'phoneNumber',
      // eslint-disable-next-line react/no-unstable-nested-components -- accepted
      customValue: () => {
        if (user.phoneNumbers && user.phoneNumbers.length > 0) {
          const phoneNumber = user.phoneNumbers?.[0];
          const phoneNumberVerificationNotSuccessful =
            phoneNumber?.status !== PhoneNumberVerificationStatus.SUCCESSFUL;

          return (
            <>
              <div className="flex flex__ai--center">
                <p>{`${phoneNumber?.countryCode} ${phoneNumber?.number}`}</p>
                {phoneNumberVerificationNotSuccessful ? (
                  <Badge className="m-left-m" size="small" color="orange">
                    {t('userManagement.phoneNumberNotVerified')}
                  </Badge>
                ) : (
                  <Badge className="m-left-m" size="small" color="green">
                    {t('userManagement.phoneNumberVerified')}
                  </Badge>
                )}
              </div>
              {phoneNumberVerificationNotSuccessful &&
              user.email === email &&
              canEditOwnName ? (
                <div className="flex flex__ai--center">
                  <Button
                    onClick={onVerifyPhoneNumberClick}
                    type="button"
                    variant="tertiary"
                  >
                    <p
                      className="color-blue-500"
                      style={{ fontSize: '16px', cursor: 'pointer' }}
                    >
                      {t('verifyPhoneNumber.title')}
                    </p>
                  </Button>
                </div>
              ) : null}
            </>
          );
        }
        return '-';
      },
    },
    ...displayedUserFields.slice(3, displayedUserFields.length),
  ];

  const lastUpdatedField: UserDisplayField = {
    label: t('userManagement.lastUpdated'),
    name: 'customField',
    customValue: () =>
      user.updatedBy
        ? `by ${user.updatedBy} on ${
            user.updatedAt ? getLocalizedDate(user.updatedAt) : ''
          }`
        : null,
  };

  return (
    <>
      <InfoList.Content>
        {displayedUserFields.map(({ label, name, customValue }) => (
          <InfoItem
            key={label}
            label={label}
            value={
              // eslint-disable-next-line no-nested-ternary -- acceptable here
              user
                ? // eslint-disable-next-line security/detect-object-injection -- we know the values
                  customValue
                  ? (customValue(user) as ReactNode)
                  : (user[name as keyof Omit<User, 'phoneNumbers'>] as ReactNode)
                : undefined
            }
          />
        ))}
      </InfoList.Content>

      {user.updatedBy && (
        <InfoList.Content>
          <InfoItem
            label={lastUpdatedField.label}
            value={
              lastUpdatedField.customValue
                ? (lastUpdatedField.customValue(user) as ReactNode)
                : undefined
            }
          />
        </InfoList.Content>
      )}

      {canEdit && (
        <div className="actions-buttons-wrapper">
          <Button size="small" onClick={onEdit}>
            {t('shared.edit')}
          </Button>
        </div>
      )}
    </>
  );
}
